import { jwtDecode } from "jwt-decode";
import { API_URL } from "./constants";
import t from "./en";
import { getToken, clearToken } from "./tokenStorage";

const getHeaders = (body) => {
  const token = getToken();

  const headers = {
    "Content-Type": body instanceof FormData ? null : "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  return headers;
};

const setSearchParams = (elems) => {
  const params = new URLSearchParams();

  if (elems && typeof elems === "object") {
    for (let key in elems) {
      elems[key] && params.append(key, elems[key]);
    }
  }

  const appGroup = JSON.parse(sessionStorage.getItem("appGroupId"));

  if (appGroup?.id) {
    params.append("appGroupId", appGroup?.id);
  }

  return params.toString();
};

const NDA_PENDING_KEY = "c2a:nda:pending";

const onResponse = (res, callback) => {
  validateResponse(res);

  const contentType = res.headers.get("Content-Type");

  if (res.ok) {
    if (contentType?.match("application/json")) {
      return res.json().then((data) => callback(data, res.status));
    } else if (contentType?.match("text/plain") || !contentType) {
      return res.text().then((data) => callback(data, res.status));
    } else if (
      contentType?.match("application/xml") ||
      contentType?.match("text/html")
    ) {
      return res.text().then((data) => callback(data, res.status));
    } else {
      return res.blob().then((data) => callback(data, res.status));
    }
  } else {
    return res.text().then((data) => {
      let errMsg = t.errorMsg;
      let parsed = null;

      if (data) {
        try {
          parsed = JSON.parse(data);
          if (parsed?.message) {
            errMsg = parsed.message;
          }
        } catch (e) {
          console.error("Error parsing response data:", e);
        }
      }

      // SCK-635 slice B — Google-link gate. Auth service returns
      //   401 {"error":"google_link_required","reason":"...","email":"<addr>"}
      // when the caller signed in with Google but the matching row is
      // LOCAL and carries no google_sub. The only way out is to sign in
      // with the local password first and then link Google from
      // Settings → Security. We do NOT clear the token here (the caller
      // never had one on the Google-signin path) — we just bounce to
      // /login with a hint the banner reads. The caller's callback still
      // gets a tagged error so its "signing in" spinner stops.
      if (res.status === 401 && parsed?.error === "google_link_required") {
        const email = parsed?.email || "";
        const target =
          `/login?linkGoogle=1${
            email ? `&email=${encodeURIComponent(email)}` : ""
          }`;
        if (
          window.location.pathname !== "/login" ||
          !window.location.search?.includes("linkGoogle=1")
        ) {
          window.location.assign(target);
        }
        const err = {
          status: res.status,
          message: parsed?.reason || "Google account is not linked yet",
          googleLinkRequired: true,
          email,
        };
        throw err;
      }

      // SCK-606 NDA gate: coord returns 403 with body
      // {"error":"nda_required","currentVersion":"<v>"} on any protected
      // endpoint until the caller records acceptance of the current NDA
      // version. Stash where the user was headed so /nda can return
      // them after acceptance, then bounce to /nda. Do NOT retry the
      // original request — the caller's callback still receives an
      // error object so it can stop rendering "in progress" UI; the
      // AcceptNdaPage owns the completion flow.
      if (res.status === 403 && parsed?.error === "nda_required") {
        try {
          const here =
            window.location.pathname + (window.location.search || "");
          if (
            window.location.pathname !== "/nda" &&
            window.location.pathname !== "/login"
          ) {
            sessionStorage.setItem(
              NDA_PENDING_KEY,
              JSON.stringify({ path: here })
            );
          }
        } catch (e) {
          /* sessionStorage unavailable — proceed with redirect anyway */
        }

        if (window.location.pathname !== "/nda") {
          window.location.assign("/nda");
        }

        const err = {
          status: res.status,
          message: "NDA acceptance required",
          nda: true,
        };
        throw err;
      }

      // SCK-706: no_membership routes to onboarding. Coord returns
      //   403 {"error":"no_membership", ...}
      // when the caller is authenticated but has not been attached
      // to any account / workspace yet. The user's forward path is
      // an invitation redemption (/redeem?token=...) or an operator
      // request — both live on the public /onboarding page. Don't
      // stash a return path: /onboarding is a terminal landing, not
      // a modal step; the caller's callback still receives a tagged
      // error so it can stop rendering "in progress" UI.
      if (res.status === 403 && parsed?.error === "no_membership") {
        if (window.location.pathname !== "/onboarding") {
          window.location.assign("/onboarding");
        }
        const err = {
          status: res.status,
          message: parsed?.message || "No workspace membership yet",
          noMembership: true,
        };
        throw err;
      }

      const err = {
        status: res.status,
        message: errMsg,
      };

      throw err;
    });
  }
};

export const GET = (endpoint, params, callback, api) => {
  const searchParamsToString = setSearchParams(params);

  fetch(
    [
      api ? api : API_URL,
      endpoint,
      searchParamsToString ? "?" : "",
      searchParamsToString,
    ].join(""),
    {
      method: "GET",
      headers: getHeaders(null),
    }
  )
    .then((res) => onResponse(res, callback))
    .catch((error) => callback(error));
};

export const POST = (endPoint, body, callback, api) => {
  const searchParamsToString = setSearchParams();

  fetch(
    (api ? api : API_URL) +
      endPoint +
      (searchParamsToString ? `?${searchParamsToString}` : ""),
    {
      method: "POST",
      headers: getHeaders(body),
      body,
    }
  )
    .then((res) => onResponse(res, callback))
    .catch((error) => callback(error));
};

export const PUT = (endPoint, body, callback, api) => {
  const searchParamsToString = setSearchParams();

  fetch(
    (api ? api : API_URL) +
      endPoint +
      (searchParamsToString ? `?${searchParamsToString}` : ""),
    {
      method: "PUT",
      headers: getHeaders(body),
      body,
    }
  )
    .then((res) => onResponse(res, callback))
    .catch((error) => callback(error));
};

/**
 * resetUserNda(userId, email)
 *
 * Admin-only: POST /admin/users/{userId}/reset-nda. On success the user's
 * `ndaAcceptedAt` is cleared server-side and they will re-see the NDA
 * gate on their next authenticated request (SCK-606 flow).
 *
 * Returns a promise that resolves with `{ message }` — a caller-ready
 * success string — and rejects with `{ status, message }` where message
 * is a friendly per-status string (403 / 404 / other). Follows the
 * `actions.js` house style: no toast/UI here, the caller wires the
 * message into its own Snackbar. SCK-714.
 */
export const resetUserNda = (userId, email) => {
  const label = email || `user ${userId}`;
  return new Promise((resolve, reject) => {
    // POST(...) invokes its callback either with `(data, httpStatus)` on
    // success or with `(errorObject)` on non-ok (via .catch). We branch
    // on the 2nd arg being a number to avoid mis-classifying a success
    // body that happens to carry a `status` field.
    POST(`/admin/users/${userId}/reset-nda`, null, (dataOrError, httpStatus) => {
      const ok = typeof httpStatus === "number" && httpStatus >= 200 && httpStatus < 300;
      if (ok) {
        resolve({
          message: `NDA acceptance reset for ${label}. They'll re-accept on next login.`,
        });
        return;
      }
      const status = dataOrError?.status ?? httpStatus;
      let message;
      if (status === 403) {
        message = "You don't have permission to reset NDA for this user.";
      } else if (status === 404) {
        message = "User not found.";
      } else {
        message = `Couldn't reset NDA for ${label}. ${dataOrError?.message || "Please try again."}`;
      }
      reject({ status, message });
    });
  });
};

export const DELETE = (endPoint, callback, api, params) => {
  const searchParamsToString = setSearchParams(params);

  fetch(
    (api ? api : API_URL) +
      endPoint +
      (searchParamsToString ? `?${searchParamsToString}` : ""),
    {
      method: "DELETE",
      headers: getHeaders(null),
    }
  )
    .then((res) => onResponse(res, callback))
    .catch((error) => {
      callback(error);
    });
};

const validateResponse = (res) => {
  if (res.status !== 401) return;

  const token = getToken();
  if (!token) return;

  // A 401 can be a transient server/proxy hiccup (clock skew after an
  // iPad wakes from sleep, a brief gateway blip, etc). Only treat it as
  // "session actually expired" — and force a re-auth — when the JWT's
  // own exp claim says so. Otherwise a single stray 401 would wipe a
  // perfectly valid session and bounce the user into a login loop.
  try {
    const { exp } = jwtDecode(token);
    if (!exp || exp * 1000 > Date.now()) return;
  } catch {
    // Undecodable token — fall through and treat it as invalid.
  }

  clearToken();
  window.location.assign("/login");
};
